import {Action, Card, DataStatus, DropdownActionMenuItem} from 'platform/components';
import {Hide, Show} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {buildArray, composePath, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useEditCustomer} from '../../hooks/useEditCustomer';
import {CustomerInformation} from './components/CustomerInformation';
import {CustomerMatchOrCreate} from './components/CustomerMatchOrCreate';
import {SecondStepComponentType} from './types/SecondStepComponentType';

export interface CustomerWidgetCardProps extends TestIdProps {
  customerId?: string | null;
  customTitle?: string;
  customActions?: DropdownActionMenuItem[];
  secondStepComponentType: SecondStepComponentType;
  onCustomer: (customer: CustomerResponseBodyV2) => void;
  isLoadingOnCustomer?: boolean;
  isLoading?: boolean;
  onChange?: VoidFunction;
  resourceId?: string;
  recordId?: string;
  isDetailLinkVisible?: boolean;
  isContactPersonHidden?: boolean;
  hasVerification?: boolean;
  vehicleId?: string | Nullish;
  tooltip?: string;
  canChangeCustomer?: boolean;
}

export function CustomerWidgetCard(props: CustomerWidgetCardProps) {
  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerV2Query({customerId: props.customerId ?? ''}, {skip: isNil(props.customerId)});
  const editCustomer = useEditCustomer(customer, props.onCustomer);

  const actions = buildArray<Action>()
    .when(isNotNilOrEmpty(props.customActions), {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_vert',
      severity: 'informational',
      menuItems: props.customActions ?? [],
    })
    .when(props.isDetailLinkVisible, {
      title: i18n.t('general.labels.detail'),
      leftIcon: 'action/open_in_new',
      variant: 'link',
      type: 'button',
      onClick: () =>
        window.open(
          composePath(customerRoutes.detail, {
            params: {id: props.customerId},
            isSearchParamsPreserved: false,
          })
        ),
    })
    .when(isNotNil(props.onChange), {
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.change'),
      isDisabled: props.canChangeCustomer === false,
      leftIcon: 'action/autorenew',
      onClick: props.onChange,
    })
    .add({
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.edit'),
      leftIcon: 'image/edit',
      isLoading: props.isLoadingOnCustomer,
      isDisabled: props.canChangeCustomer === false,
      onClick: editCustomer,
    });

  return (
    <Card
      actions={isNotNil(props.customerId) ? actions : undefined}
      title={props.customTitle || i18n.t('entity.customer.labels.customer')}
      tooltip={props.tooltip}
      data-testid={suffixTestId('header', props)}
    >
      <DataStatus isLoading={props.isLoading} minHeight={100}>
        <Hide when={props.customerId}>
          <CustomerMatchOrCreate
            onCustomer={props.onCustomer}
            data-testid={suffixTestId('finder', props)}
            vehicleId={props.vehicleId}
          />
        </Hide>
        <DataStatus isLoading={isLoading} isError={isError}>
          <Show when={customer}>
            <CustomerInformation
              secondStepComponentType={props.secondStepComponentType}
              isContactPersonHidden={props.isContactPersonHidden}
              customer={customer!}
              resourceId={props.resourceId}
              recordId={props.recordId}
              hasVerification={props.hasVerification}
              data-testid={suffixTestId('information', props)}
            />
          </Show>
        </DataStatus>
      </DataStatus>
    </Card>
  );
}
